import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { CATEGORY_CONFIG } from '@eliadmin/core/src/constants/category-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import type { PropsWithChildren, ReactNode } from 'react';
import React from 'react';

export type CategoryCardProps = {
  href: string;
  className?: { root?: string; content?: string };
};

export function CategoryCard(props: PropsWithChildren<CategoryCardProps>) {
  const LinkComp = props.href ? (isRelativeHref(getSafeHref(props.href)) ? Link : 'a') : 'span';

  return (
    <Card
      asChild
      // TODO: fix secondary colors
      className={cn('h-[56px] hover:bg-secondary/80', 'ea-category-card', props.className?.root)}
      variant="outlined"
    >
      <LinkComp href={props.href ? getSafeHref(props.href) : ((undefined as any) ?? '#')}>
        <CardContent
          className={cn(
            'line-clamp-2 flex h-full w-full flex-row items-center gap-4 text-ellipsis p-3',
            'ea-category-card__content',
            props.className?.content,
          )}
        >
          {props.children}
        </CardContent>
      </LinkComp>
    </Card>
  );
}

type CategoryCardImageProps = {
  url: string;
  alt?: string;
  className?: {
    root?: string;
    image?: string;
  };
};
export function CategoryCardImage(props: CategoryCardImageProps) {
  return (
    <div className={cn('flex-shrink-0', 'ea-category-card-image__root', props.className?.root)}>
      <Image
        alt={resolveImageAlt(props, 'category-image')}
        src={props.url}
        width={props.className?.image ? '0' : 32}
        height={props.className?.image ? '0' : 32}
        sizes={props.className?.image ? '100vw' : '32px'}
        className={cn(
          'h-8 w-8 object-contain',
          'ea-category-card-image__image',
          props.className?.image,
        )}
      />
    </div>
  );
}

export function CategoryCardName(props: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn('text-left text-sm', 'ea-category-card-name', props.className)}>
      <span>{props.children}</span>
    </div>
  );
}

export function CategoryCardDescription(props: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn('pt-4 text-left text-xs', 'ea-category-card-description', props.className)}>
      <span>{props.children}</span>
    </div>
  );
}

export function CategoryCardArrow(props: { className?: string; icon?: ReactNode }) {
  return (
    <div
      className={cn(
        'flex flex-1 justify-end text-left text-foreground',
        'ea-category-card-arrow',
        props.className,
      )}
    >
      {React.cloneElement((props.icon || CATEGORY_CONFIG.categoryCard.arrowIcon) as any, {
        className: cn('w-4 h-4', 'ea-category-card-arrow__icon'),
      })}
    </div>
  );
}
